/**
 * 회사가 쓸 AI 고르기 — 8443 통합설정 (판 H-③).
 *
 * 서버 SSOT: `apps/chat-server/src/modules/admin/company-ai.routes.ts`.
 *
 * ★ 고를 수 있는 것은 **시스템 관리자가 8444 콘솔에서 «연» 목록**뿐이다.
 *   여기에는 키·엔드포인트가 오지 않는다 — 이름과 열쇠만 온다.
 *
 * ## ★★★ 판 K — 하나가 아니라 «여럿»이다
 *
 * 자리마다 하나만 고르면 **사용자가 대화에서 고를 것이 없다**. 회사가 여럿을 지정하고
 * 그중 **기본 하나**를 정하면, 사용자는 그 안에서 고른다.
 */
import { apiClient } from './client';

/** 자리 — 서버의 `COMPANY_AI_SURFACES` 와 같은 낱말. */
export type AiSurface = 'ai-menu' | 'chat';

export interface AiChoice {
  /** `cat.<연결id>::<모델>`. 저장하는 값이 이것이다. */
  key: string;
  label: string;
  kind: string;
}

/** 판 K — 우리 회사가 고른 것 한 줄. */
export interface AiPicked {
  key: string;
  isDefault: boolean;
  /** 지금도 열려 있나. 목록을 못 받았으면 null. */
  stillOpen: boolean | null;
}

export interface SurfaceState {
  surface: AiSurface;
  /**
   * 고를 수 있는 것.
   * ★ `null` 은 **「목록을 못 받아 왔다」**이고 `[]` 는 **「열린 것이 없다」**다 — 다른 사실이다.
   */
  available: AiChoice[] | null;
  /**
   * 판 K — 우리 회사가 고른 것들. **여럿**이다.
   * 사용자는 대화에서 이 안에서 고르고, 안 고르면 `defaultKey` 로 간다.
   */
  selected: AiPicked[];
  defaultKey: string | null;
}

export async function getCompanyAi(): Promise<SurfaceState[]> {
  const { data } = await apiClient.get<{ surfaces: SurfaceState[] }>('/admin/company-ai');
  return Array.isArray(data?.surfaces) ? data.surfaces : [];
}

/**
 * 판 K — 자리별로 **여럿** 고르고 그중 기본 하나를 정한다.
 * ★ 빈 배열 = 「안 정함」으로 되돌린다(운영자 기본을 따른다).
 * ★ 기본이 고른 것 밖이면 **서버가 첫 번째로 바로잡는다** — 화면이 지어내지 않는다.
 */
export async function setCompanyAi(
  surface: AiSurface,
  keys: string[],
  defaultKey: string | null,
): Promise<void> {
  await apiClient.put('/admin/company-ai', { surface, keys, default_key: defaultKey });
}

/* ── 판 L — 우리 회사 총사용량 ─────────────────────────────────────────────
   「회사의 총사용량은 통합설정에서 AI 연결 메뉴에 나왔으면 한다. 그래프로 나와라.
    에이전트별, 모델별, 월, 주, 일, 사용자별, 부서별, 회사별」(2026-09-10 사용자). */

export type UsageUnit = 'day' | 'week' | 'month' | 'year';

export interface UsageBucket {
  key: string;
  label: string;
  promptTokens: number;
  completionTokens: number;
  calls: number;
}

export interface CompanyUsage {
  days: number;
  from: string;
  unit: UsageUnit;
  total: { promptTokens: number; completionTokens: number; calls: number };
  byInstitution: UsageBucket[];
  byModel: UsageBucket[];
  byConnection: UsageBucket[];
  byAgent: UsageBucket[];
  byUser: UsageBucket[];
  byOrg: UsageBucket[];
  series: Array<{ bucket: string; tokens: number; calls: number }>;
}

export async function getCompanyUsage(unit: UsageUnit): Promise<CompanyUsage> {
  const { data } = await apiClient.get<CompanyUsage>('/admin/company-ai/usage', { params: { unit } });
  return data;
}

/* ── 판 M — 토큰 한도 ──────────────────────────────────────────────────────
   「회사 관리자는 사원별 기본 토큰과 사원별 토큰을 관리해야 한다」(2026-09-10 사용자).
   ★★★ 넘어도 **막지 않는다** — 계량이 CLI 를 못 재기 때문(그때만 규칙이 참이다). */

export interface QuotaState {
  limit: string | null;
  source: 'user' | 'company-default' | 'none';
  usedThisMonth: number;
  remaining: number | null;
  over: boolean;
  percent: number | null;
  month: string;
}

export interface MemberQuota {
  userId: string;
  name: string;
  /** 이 사람에게 실제로 걸린 한도. null = 무제한. */
  limit: string | null;
  source: 'user' | 'company-default' | 'none';
  usedThisMonth: number;
  over: boolean;
  memo: string;
}

export interface CompanyQuota {
  /** 시스템 관리자가 정한 회사 한도. */
  company: QuotaState & { memberDefault: string | null };
  memberDefault: string | null;
  month: string;
  members: MemberQuota[];
}

export async function getCompanyQuota(): Promise<CompanyQuota> {
  const { data } = await apiClient.get<CompanyQuota>('/admin/company-ai/quota');
  return data;
}

/** 사원 «기본» 한도. `null` = 무제한. */
export async function setMemberDefault(memberDefault: number | null): Promise<void> {
  await apiClient.put('/admin/company-ai/quota/default', { member_default: memberDefault });
}

/**
 * 사원별 한도.
 * ★ `mode` 로 세 상태를 «명시»한다 — 숫자 하나로는 「기본을 따른다」와 「무제한」을 못 가른다.
 */
export async function setMemberQuota(
  userId: string,
  mode: 'default' | 'unlimited' | 'limit',
  tokens?: number,
  memo?: string,
): Promise<void> {
  await apiClient.put('/admin/company-ai/quota/member', {
    user_id: userId, mode,
    ...(tokens !== undefined ? { tokens } : {}),
    ...(memo !== undefined ? { memo } : {}),
  });
}
